import { v4 as uuidv4 } from 'uuid';
import NewRelicBrowser from '@/plugins/newrelic-browser.ts';

const STORAGE_KEY = 'asset-recovery:incident';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_EVENTS = 32;
const PHASES = [
    'detected',
    'reload_requested',
    'blocked',
    'booted',
    'route_resolved',
    'shell_ready',
    'ready',
    'failed',
] as const;
type Phase = (typeof PHASES)[number];
type Reason = 'cooldown' | 'storage_read' | 'storage_write' | 'repeat_asset_failure';
type Route = { name?: string | symbol | null; path: string };
type RecoveryEvent = {
    eventId: string;
    phase: Phase;
    occurredAt: number;
    currentRelease: string;
    routeName: string;
    reason: string;
    attemptCount: number;
};
type Incident = {
    schema: 1;
    recoveryId: string;
    fromRelease: string;
    startedAt: number;
    tabAgeMs: number;
    source: 'vite' | 'router';
    failureKind: 'css' | 'module' | 'unknown';
    assetPath: string;
    currentRouteName: string;
    intendedRouteName: string;
    targetPath: string;
    events: RecoveryEvent[];
};

const release = () => process.env.VUE_APP_VERSION || 'unknown';
const safeName = (value: unknown): string =>
    typeof value === 'string' && /^[\w.@-]{1,160}$/.test(value) ? value : 'unknown';
const safeAssetPath = (value: unknown): string =>
    typeof value === 'string' &&
    /^\/(js|css|assets)\/[\w/.@-]+\.(js|mjs|css)$/.test(value) &&
    value.length <= 240
        ? value
        : 'unknown';
const isId = (value: unknown): value is string =>
    typeof value === 'string' && /^[a-f\d-]{36}$/i.test(value);
const normalizedPath = (path: string): string => path.replace(/\/$/, '') || '/';

function errorMessage(error: unknown): string {
    if (error instanceof Error) {
        return error.message;
    }
    return typeof error === 'string' ? error : '';
}

function failureKind(message: string): Incident['failureKind'] {
    if (/Unable to preload CSS/i.test(message)) {
        return 'css';
    }
    return /import.*module|module.*script/i.test(message) ? 'module' : 'unknown';
}

let incident: Incident | null = null;
let currentRoute: Route = { path: globalThis.location.pathname };
let intendedRoute: Route | null = null;
let restored = false;
let reloadRequested = false;
const sent = new Set<string>();
const recorded = new Set<string>();

function safely(callback: () => void): void {
    try {
        callback();
    } catch {
        // Observability must never prevent the existing recovery path from running.
    }
}

function persist(): void {
    safely(() => globalThis.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(incident)));
}

function flush(): void {
    if (!incident || !NewRelicBrowser.agent) {
        return;
    }
    const active = incident;
    active.events.forEach((event) => {
        if (sent.has(event.eventId)) {
            return;
        }
        safely(() => {
            NewRelicBrowser.addPageAction('asset_recovery', {
                ...event,
                recoveryId: active.recoveryId,
                fromRelease: active.fromRelease,
                elapsedMs: Math.max(0, event.occurredAt - active.startedAt),
                tabAgeMs: active.tabAgeMs,
                source: active.source,
                failureKind: active.failureKind,
                assetPath: active.assetPath,
                currentRouteName: active.currentRouteName,
                intendedRouteName: active.intendedRouteName,
                readinessScope: event.phase === 'ready' ? 'lead_paint' : 'none',
            });
            sent.add(event.eventId);
        });
    });
}

function record(phase: Phase, reason = '', routeName = safeName(currentRoute.name)): void {
    if (!incident) {
        return;
    }
    const key = `${phase}:${reason}:${routeName}`;
    if (recorded.has(key)) {
        return;
    }
    recorded.add(key);
    const attemptCount =
        (incident.events.at(-1)?.attemptCount ?? 0) + (phase === 'reload_requested' ? 1 : 0);
    incident.events.push({
        eventId: uuidv4(),
        phase,
        reason,
        routeName,
        attemptCount,
        occurredAt: Date.now(),
        currentRelease: safeName(release()),
    });
    incident.events = incident.events.slice(-MAX_EVENTS);
    // Persist the first detection together with the guard's decision, so telemetry
    // storage cannot consume quota before the existing reload marker is written.
    if (phase !== 'detected') {
        persist();
    }
    flush();
}

function restore(): void {
    safely(() => {
        const value = JSON.parse(globalThis.sessionStorage.getItem(STORAGE_KEY) || 'null');
        if (
            value?.schema !== 1 ||
            !isId(value.recoveryId) ||
            !Number.isFinite(value.startedAt) ||
            Date.now() - value.startedAt > MAX_AGE_MS ||
            value.startedAt > Date.now() ||
            !Number.isFinite(value.tabAgeMs) ||
            !['vite', 'router'].includes(value.source) ||
            !['css', 'module', 'unknown'].includes(value.failureKind) ||
            typeof value.targetPath !== 'string' ||
            !value.targetPath.startsWith('/') ||
            /[?#]/.test(value.targetPath) ||
            value.targetPath.length > 2048 ||
            !Array.isArray(value.events) ||
            value.events.length > MAX_EVENTS
        ) {
            return;
        }
        const events: RecoveryEvent[] = [];
        for (const event of value.events) {
            if (
                !event ||
                !isId(event.eventId) ||
                !PHASES.includes(event.phase) ||
                !Number.isFinite(event.occurredAt) ||
                !Number.isSafeInteger(event.attemptCount) ||
                event.attemptCount < 0
            ) {
                return;
            }
            events.push({
                eventId: event.eventId,
                phase: event.phase,
                occurredAt: event.occurredAt,
                attemptCount: event.attemptCount,
                currentRelease: safeName(event.currentRelease),
                routeName: safeName(event.routeName),
                reason: [
                    'cooldown',
                    'storage_read',
                    'storage_write',
                    'repeat_asset_failure',
                ].includes(event.reason)
                    ? event.reason
                    : '',
            });
        }
        incident = {
            schema: 1,
            recoveryId: value.recoveryId,
            startedAt: value.startedAt,
            tabAgeMs: Math.max(0, value.tabAgeMs),
            source: value.source,
            failureKind: value.failureKind,
            assetPath: safeAssetPath(value.assetPath),
            fromRelease: safeName(value.fromRelease),
            currentRouteName: safeName(value.currentRouteName),
            intendedRouteName: safeName(value.intendedRouteName),
            targetPath: value.targetPath,
            events,
        };
        flush();
        restored =
            (events.at(-1)?.attemptCount ?? 0) > 0 &&
            !events.some((event) => event.phase === 'ready');
        if (restored) {
            record('booted');
        }
    });
}

export function observeAssetFailure(error: unknown, source: 'vite' | 'router'): void {
    safely(() => {
        if (reloadRequested) {
            return;
        }
        const target = intendedRoute || currentRoute;
        if (
            !incident ||
            normalizedPath(incident.targetPath) !== normalizedPath(target.path) ||
            Date.now() - incident.startedAt > MAX_AGE_MS ||
            incident.events.some((event) => event.phase === 'ready')
        ) {
            const message = errorMessage(error);
            const match = /https?:\/\/[^\s"'<>]+|\/(?:js|css|assets)\/[^\s"'<>]+/.exec(message);
            let assetPath = 'unknown';
            if (match) {
                const url = new URL(match[0], globalThis.location.origin);
                if (url.origin === globalThis.location.origin) {
                    assetPath = safeAssetPath(url.pathname);
                }
            }
            incident = {
                schema: 1,
                recoveryId: uuidv4(),
                fromRelease: safeName(release()),
                startedAt: Date.now(),
                tabAgeMs: Math.round(performance.now()),
                source,
                failureKind: failureKind(message),
                assetPath,
                currentRouteName: safeName(currentRoute.name),
                intendedRouteName: safeName(target.name),
                targetPath: target.path,
                events: [],
            };
            recorded.clear();
            sent.clear();
            restored = false;
        } else if (restored) {
            record('failed', 'repeat_asset_failure');
        }
        record('detected');
    });
}

export function observeAssetReload(): void {
    safely(() => {
        record('reload_requested');
        reloadRequested = true;
    });
}

export function observeAssetRecoveryBlocked(reason: Reason): void {
    safely(() => {
        if (!reloadRequested) {
            record('blocked', reason);
        }
    });
}

export function startAssetRecoveryNavigation(to: Route, from: Route): void {
    currentRoute = from;
    intendedRoute = to;
}

export function cancelAssetRecoveryNavigation(to: Route): void {
    if (intendedRoute === to) {
        intendedRoute = null;
    }
}

export function resolveAssetRecoveryNavigation(to: Route): void {
    currentRoute = to;
    intendedRoute = null;
    safely(() => {
        if (restored) {
            record('route_resolved');
        }
    });
}

export function observeAssetRecoveryShellReady(route: Route): void {
    safely(() => {
        if (restored && !reloadRequested) {
            record('shell_ready', '', safeName(route.name));
        }
    });
}

export function observeAssetRecoveryLeadReady(leadId?: string | number): void {
    safely(() => {
        if (
            !restored ||
            reloadRequested ||
            !incident ||
            leadId == null ||
            currentRoute.name !== 'leads.update' ||
            normalizedPath(currentRoute.path) !== `/leads/${encodeURIComponent(String(leadId))}` ||
            normalizedPath(currentRoute.path) !== normalizedPath(incident.targetPath)
        ) {
            return;
        }
        record('ready');
        restored = false;
    });
}

restore();
